<template>
  <div class="p-6 flex flex-col h-full overflow-hidden">
    <div class="flex items-start justify-between gap-4 mb-4 shrink-0">
      <div>
        <h2 class="text-xl font-semibold">Timeline</h2>
        <div class="text-sm text-gray-500">
          Tenant-wide events across users and platform features.
        </div>
      </div>
      <div class="flex items-center gap-2">
        <Button
          label="Export Excel"
          icon="pi pi-file-excel"
          severity="success"
          outlined
          :disabled="loading || exporting || exportBuilding"
          @click="exportToExcel"
        />
        <Button
          label="Refresh"
          icon="pi pi-refresh"
          severity="secondary"
          :loading="loading"
          @click="refresh"
        />
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4 shrink-0">
      <div class="surface-card border-round p-3 shadow-1">
        <div class="text-sm text-gray-500">Total (filtered)</div>
        <div class="text-2xl font-bold">{{ total ?? '—' }}</div>
      </div>
      <div class="surface-card border-round p-3 shadow-1">
        <div class="text-sm text-gray-500">This page</div>
        <div class="text-2xl font-bold">{{ events.length }}</div>
        <div v-if="total > 0" class="text-xs text-gray-500 mt-1">
          {{ pageRangeLabel }}
        </div>
      </div>
      <div class="surface-card border-round p-3 shadow-1 min-h-[220px] flex flex-col">
        <div class="text-sm text-gray-500 shrink-0 mb-1">By category</div>
        <div v-if="categoryChartData" class="flex-1 min-h-[180px] relative">
          <Chart
            :key="categoryChartKey"
            type="doughnut"
            :data="categoryChartData"
            :options="categoryChartOptions"
            class="absolute inset-0"
          />
        </div>
        <div v-else class="text-gray-400 text-sm flex-1 flex items-center justify-center py-6">
          —
        </div>
      </div>
      <div class="surface-card border-round p-3 shadow-1">
        <div class="text-sm text-gray-500">Pages</div>
        <div class="text-sm">
          <div>
            <span class="text-gray-500">Page</span>
            <span class="font-medium"> {{ page }} </span>
            <span class="text-gray-500">of</span>
            <span class="font-medium"> {{ totalPages || 1 }}</span>
          </div>
          <div class="text-xs text-gray-500 mt-1">{{ pageSize }} per page</div>
        </div>
      </div>
    </div>

    <div class="bg-white border border-gray-200 rounded-lg p-3 mb-3 shrink-0">
      <div class="flex flex-wrap items-end gap-3">
        <div class="flex flex-col">
          <label class="text-sm font-medium text-gray-700 mb-1">Date Range</label>
          <Calendar
            v-model="dateRange"
            selectionMode="range"
            dateFormat="yy-mm-dd"
            placeholder="Select date range"
            class="w-64"
          />
        </div>
        <div class="flex flex-col">
          <label class="text-sm font-medium text-gray-700 mb-1">Categories</label>
          <MultiSelect
            v-model="selectedCategories"
            :options="categoryOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="All categories"
            display="chip"
            class="w-72"
          />
        </div>
        <div class="flex flex-col">
          <label class="text-sm font-medium text-gray-700 mb-1">Search user</label>
          <InputText v-model="userQuery" placeholder="Email or name" class="w-64" />
        </div>
        <div class="flex items-center gap-2 ml-auto">
          <Button
            label="Apply"
            icon="pi pi-search"
            :loading="loading"
            @click="applyFilters"
          />
          <Button
            label="Clear"
            icon="pi pi-times"
            severity="secondary"
            :disabled="loading"
            @click="clearFilters"
          />
        </div>
      </div>
    </div>

    <div class="surface-card border-round shadow-1 overflow-hidden flex-1 min-h-0 flex flex-col">
      <DataTable
        :value="events"
        dataKey="_id"
        responsiveLayout="scroll"
        class="p-datatable-sm"
        :loading="loading"
        :paginator="false"
        scrollable
        scrollHeight="flex"
        v-model:expandedRows="expandedRows"
      >
      <template #empty>
        <div class="text-center py-12 text-gray-500">
          <i class="pi pi-clock text-4xl text-gray-300 mb-2 block"></i>
          No events match your filters. Try adjusting the date range or search.
        </div>
      </template>
      <Column :expander="true" headerStyle="width: 3rem" />
      <Column header="Timestamp" style="min-width: 190px">
        <template #body="{ data: row }">
          <span class="font-mono text-xs">{{ formatTimestamp(row.timestamp) }}</span>
        </template>
      </Column>
      <Column header="User" style="min-width: 260px">
        <template #body="{ data: row }">
          <div class="flex items-center gap-2 min-w-0">
            <div class="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
              <img
                v-if="row.user?.picture_profile?.small"
                :src="row.user.picture_profile.small"
                class="w-7 h-7 object-cover"
                alt=""
              />
              <i v-else class="pi pi-user text-gray-400 text-sm"></i>
            </div>
            <div class="min-w-0">
              <div class="text-sm font-medium truncate">{{ row.user?.name || row.userId }}</div>
              <div class="text-xs text-gray-500 truncate">{{ row.user?.email || '—' }}</div>
            </div>
          </div>
        </template>
      </Column>
      <Column header="Category" style="width: 160px">
        <template #body="{ data: row }">
          <Tag :value="row.category" :severity="categorySeverity(row.category)" />
        </template>
      </Column>
      <Column header="Event" style="min-width: 220px">
        <template #body="{ data: row }">
          <span class="text-sm">{{ row.title || row.eventType }}</span>
        </template>
      </Column>
      <template #expansion="{ data: row }">
        <div class="p-3 bg-gray-50 border border-gray-200 rounded">
          <div class="text-sm font-semibold mb-2">Details</div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
            <CopyableField
              v-for="e in metaEntries(row)"
              :key="e.k"
              :label="e.k"
              :value="String(e.v)"
            />
          </div>
          <div
            v-if="row.metadata && Object.keys(row.metadata).some(k => typeof row.metadata[k] === 'object')"
            class="mt-3"
          >
            <div class="text-xs font-semibold text-gray-600 mb-1">Metadata (JSON)</div>
            <pre class="text-xs font-mono whitespace-pre-wrap break-words text-gray-700">{{ JSON.stringify(row.metadata, null, 2) }}</pre>
          </div>
        </div>
      </template>
    </DataTable>
    </div>

    <div v-if="total > 0" class="flex justify-center mt-4 shrink-0">
      <Paginator
        :first="firstRecordIndex"
        :rows="pageSize"
        :total-records="totalRecords"
        :rows-per-page-options="[10, 25, 50]"
        @page="onPaginator"
      />
    </div>

    <Teleport to="body">
      <div
        v-if="showExportOverlay"
        class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-overlay-title"
      >
        <div
          class="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-gray-200"
        >
          <h3 id="export-overlay-title" class="text-lg font-semibold text-gray-900 mb-1">
            {{ exportOverlayTitle }}
          </h3>
          <p class="text-sm text-gray-600 mb-4">
            {{ exportOverlaySubtitle }}
          </p>
          <div class="export-progress-track">
            <ProgressBar
              v-if="exportBuilding"
              mode="indeterminate"
              class="!h-2"
            />
            <ProgressBar
              v-else-if="exportProgressHasTotal"
              :value="exportStreamPercent"
              :show-value="true"
              class="!h-2"
            />
            <ProgressBar
              v-else
              mode="indeterminate"
              class="!h-2"
            />
          </div>
          <p v-if="exporting && !exportBuilding && exportLoadedCount != null" class="text-xs text-gray-500 mt-3 text-center font-mono">
            {{ exportLoadedFormatted }} / {{ exportTotalExpectedFormatted }} rows
            <span v-if="exportProgressHasTotal" class="text-gray-400">
              ({{ exportStreamPercent }}%)
            </span>
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
/* PrimeVue applies a 1s width transition; label updates immediately — bar looked stuck. */
.export-progress-track :deep(.p-progressbar-determinate .p-progressbar-value) {
  transition: none !important;
}
</style>

<script>
import { mapActions, mapState } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import Paginator from 'primevue/paginator';
import ProgressBar from 'primevue/progressbar';
import Chart from 'primevue/chart';
import CopyableField from '@/components/ContentManagement/components/CopyableField.vue';
import { getTenantFromHostname } from '@/services/tenantValidation';
import * as XLSX from 'xlsx';

/** Safe segment for download filenames (lowercase, a-z0-9 and underscores). */
function sanitizeFilenameSegment(raw) {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return s || 'tenant';
}

function sanitizeExcelSheetName(name) {
  const invalid = ['\\', '/', '*', '?', ':', '[', ']'];
  let s = String(name);
  invalid.forEach((ch) => {
    s = s.split(ch).join('_');
  });
  s = s.substring(0, 31);
  if (!s.trim()) s = 'Sheet';
  return s;
}

const EXCEL_MAX_ROWS_PER_SHEET = 1_048_576;

/** Distinct colors for timeline category doughnut (cycles if many categories). */
const CATEGORY_CHART_PALETTE = [
  '#6366f1', '#0ea5e9', '#22c55e', '#eab308', '#f97316', '#ec4899', '#8b5cf6', '#14b8a6', '#64748b', '#84cc16',
];

export default {
  name: 'TimelineManager',
  components: {
    DataTable,
    Column,
    Button,
    Calendar,
    MultiSelect,
    InputText,
    Tag,
    Paginator,
    ProgressBar,
    Chart,
    CopyableField,
  },
  data() {
    return {
      expandedRows: {},
      dateRange: null,
      selectedCategories: [],
      userQuery: '',
      exportBuilding: false,
    };
  },
  computed: {
    ...mapState('timeline', {
      events: 'events',
      loading: 'loading',
      exporting: 'exporting',
      exportProgress: 'exportProgress',
      page: 'page',
      pageSize: 'pageSize',
      total: 'total',
      totalPages: 'totalPages',
      categories: 'categories',
      stats: 'stats',
      filters: 'filters',
    }),
    /** Matches filters sent to the export API (after Apply). */
    isExportFiltered() {
      const f = this.filters;
      if (!f) return false;
      if (f.startDate || f.endDate) return true;
      if (Array.isArray(f.categories) && f.categories.length > 0) return true;
      if (f.userQuery && String(f.userQuery).trim()) return true;
      return false;
    },
    totalRecords() {
      return this.total ?? 0;
    },
    firstRecordIndex() {
      return (this.page - 1) * this.pageSize;
    },
    pageRangeLabel() {
      if (this.total === 0) return '0 of 0';
      const from = this.firstRecordIndex + 1;
      const to = this.firstRecordIndex + this.events.length;
      return `${from}–${to} of ${this.total}`;
    },
    categoryOptions() {
      const base = (this.categories || []).map(c => ({ label: c, value: c }));
      return base.length ? base : [
        { label: 'user', value: 'user' },
        { label: 'assessment', value: 'assessment' },
        { label: 'travel_buddy', value: 'travel_buddy' },
        { label: 'level', value: 'level' },
        { label: 'session', value: 'session' },
        { label: 'personal_goal', value: 'personal_goal' },
        { label: 'profile', value: 'profile' },
        { label: 'devtip', value: 'devtip' },
        { label: 'teams', value: 'teams' },
        { label: 'audit_access', value: 'audit_access' },
        { label: 'cms', value: 'cms' },
        { label: 'tenant_config', value: 'tenant_config' },
      ];
    },
    categoryChartKey() {
      const bc = this.stats?.byCategory;
      if (!bc || typeof bc !== 'object') return 'timeline-cat-empty';
      try {
        return `timeline-cat-${JSON.stringify(bc)}`;
      } catch {
        return 'timeline-cat-fallback';
      }
    },
    categoryChartData() {
      const bc = this.stats?.byCategory;
      if (!bc || typeof bc !== 'object') return null;
      const entries = Object.entries(bc)
        .filter(([, v]) => Number(v) > 0)
        .sort((a, b) => Number(b[1]) - Number(a[1]));
      if (!entries.length) return null;
      return {
        labels: entries.map(([k]) => this.formatCategoryChartLabel(k)),
        datasets: [
          {
            data: entries.map(([, v]) => Number(v)),
            backgroundColor: entries.map((_, i) => CATEGORY_CHART_PALETTE[i % CATEGORY_CHART_PALETTE.length]),
            borderWidth: 1,
            borderColor: '#ffffff',
          },
        ],
      };
    },
    categoryChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              boxWidth: 10,
              padding: 8,
              font: { size: 11 },
            },
          },
        },
      };
    },
    showExportOverlay() {
      return this.exporting || this.exportBuilding;
    },
    exportLoadedCount() {
      return this.exportProgress?.loaded ?? null;
    },
    exportLoadedFormatted() {
      const n = this.exportLoadedCount;
      return n == null ? '0' : Number(n).toLocaleString();
    },
    /** Server meta totalEstimate (adapter sum for filters); falls back to table total. */
    exportTotalExpected() {
      const fromStream = this.exportProgress?.totalEstimate;
      if (fromStream != null && Number(fromStream) > 0) return Number(fromStream);
      const t = this.total;
      return t != null && Number(t) > 0 ? Number(t) : 0;
    },
    exportTotalExpectedFormatted() {
      const n = this.exportTotalExpected;
      return n > 0 ? n.toLocaleString() : '—';
    },
    exportProgressHasTotal() {
      return this.exportTotalExpected > 0;
    },
    /** Bar width and label use the same value (no PrimeVue width transition lag). */
    exportStreamPercent() {
      if (!this.exportProgressHasTotal || this.exportLoadedCount == null) return 0;
      const loaded = Number(this.exportLoadedCount);
      const cap = this.exportTotalExpected;
      if (!cap) return 0;
      return Math.min(100, Math.round((loaded / cap) * 100));
    },
    exportOverlayTitle() {
      if (this.exportBuilding) return 'Building Excel file';
      return 'Exporting timeline';
    },
    exportOverlaySubtitle() {
      if (this.exportBuilding) {
        return 'Writing sheets and preparing your download…';
      }
      if (this.exportProgressHasTotal) {
        return 'Compared to the server row estimate for your date/category filters. User search can return fewer rows than this estimate.';
      }
      return 'Streaming events from the server…';
    },
  },
  async mounted() {
    await this.fetchCategories();
    this.syncDateRangeFromFilters();
    await this.fetchEvents({ reset: true });
  },
  methods: {
    ...mapActions('timeline', [
      'fetchEvents',
      'applyPaginator',
      'fetchCategories',
      'updateFilters',
      'resetTimeline',
      'exportExcel',
    ]),

    eventToExcelRow(ev) {
      const ts = ev.timestamp ? new Date(ev.timestamp) : null;
      const timestamp = ts && !Number.isNaN(ts.getTime()) ? ts.toISOString() : '';
      const row = {
        Timestamp: timestamp,
        UserName: ev.user?.name || '',
        UserEmail: ev.user?.email || '',
        UserId: ev.userId || '',
        EventType: ev.eventType || '',
        Title: ev.title || '',
        Severity: ev.severity || '',
        Source: ev.source || '',
        SourceId: ev.sourceId || '',
      };
      const meta = ev.metadata || {};
      Object.entries(meta).forEach(([k, v]) => {
        if (v != null && typeof v !== 'object') {
          row[`meta_${k}`] = v;
        }
      });
      return row;
    },

    getExportTenantSegment() {
      const user = this.$store.state.auth?.user;
      let raw = user?.tenant?.slug || user?.tenant?.name;
      if (!raw && user?.tenant && typeof user.tenant === 'string') {
        raw = user.tenant;
      }
      if (!raw) {
        try {
          const ls = JSON.parse(localStorage.getItem('user') || '{}');
          raw = ls.tenant?.slug || ls.tenant?.name
            || (typeof ls.tenant === 'string' ? ls.tenant : null);
        } catch (e) {
          raw = null;
        }
      }
      if (!raw) {
        raw = getTenantFromHostname() || 'tenant';
      }
      return sanitizeFilenameSegment(raw);
    },

    async exportToExcel() {
      try {
        const { events: rows, capped: exportCapped } = await this.exportExcel();
        if (!rows || !rows.length) {
          this.$toast?.add?.({
            severity: 'info',
            summary: 'No data',
            detail: 'No events match the current filters.',
            life: 4000,
          });
          return;
        }

        this.exportBuilding = true;

        const byCategory = {};
        rows.forEach((ev) => {
          const cat = ev.category || 'unknown';
          if (!byCategory[cat]) byCategory[cat] = [];
          byCategory[cat].push(ev);
        });
        const categories = Object.keys(byCategory).sort();

        const workbook = XLSX.utils.book_new();
        const usedSheetNames = new Set();

        categories.forEach((cat) => {
          const allRows = byCategory[cat].map((ev) => this.eventToExcelRow(ev));
          const maxDataRows = EXCEL_MAX_ROWS_PER_SHEET - 1;
          const chunks = [];
          for (let start = 0; start < allRows.length; start += maxDataRows) {
            chunks.push(allRows.slice(start, start + maxDataRows));
          }

          chunks.forEach((dataRows, chunkIdx) => {
            const suffix = chunks.length > 1 ? `_${chunkIdx + 1}` : '';
            let baseName = sanitizeExcelSheetName(`${cat}${suffix}`);
            let sheetName = baseName;
            let n = 1;
            while (usedSheetNames.has(sheetName)) {
              sheetName = sanitizeExcelSheetName(`${baseName}_${n}`);
              n += 1;
            }
            usedSheetNames.add(sheetName);

            const worksheet = XLSX.utils.json_to_sheet(dataRows);
            const colCount = dataRows.reduce(
              (max, r) => Math.max(max, Object.keys(r).length),
              8,
            );
            worksheet['!cols'] = Array.from({ length: colCount }, (_, i) => ({
              wch: i < 8 ? (i === 0 ? 24 : 18) : 14,
            }));
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
          });
        });

        const d = new Date();
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        const tenantSeg = this.getExportTenantSegment();
        const filteredSeg = this.isExportFiltered ? '_filtered' : '';
        const filename = `${tenantSeg}_timeline_export_${dateStr}${filteredSeg}.xlsx`;
        XLSX.writeFile(workbook, filename);

        const successDetail = exportCapped
          ? `${rows.length.toLocaleString()} event(s) saved to ${filename}. Server row cap reached — export may be partial.`
          : `${rows.length.toLocaleString()} event(s) saved to ${filename}.`;
        this.$toast?.add?.({
          severity: 'success',
          summary: 'Export complete',
          detail: successDetail,
          life: exportCapped ? 6000 : 4000,
        });
      } catch (e) {
        this.$toast?.add?.({
          severity: 'error',
          summary: 'Export failed',
          detail: e?.message || 'Unknown error',
          life: 5000,
        });
      } finally {
        this.exportBuilding = false;
      }
    },

    formatTimestamp(value) {
      if (!value) return '—';
      const d = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) return '—';
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      const ss = String(d.getSeconds()).padStart(2, '0');
      return `${dd}/${mm}/${yyyy} ${hh}:${min}:${ss}`;
    },

    categorySeverity(category) {
      const map = {
        assessment: 'info',
        level: 'success',
        travel_buddy: 'warning',
        session: 'secondary',
        user: 'info',
        personal_goal: 'info',
        profile: 'warn',
        devtip: 'info',
        teams: 'info',
        cms: 'info',
        tenant_config: 'warn',
      };
      return map[category] || 'secondary';
    },

    formatCategoryChartLabel(key) {
      const k = String(key ?? '');
      const raw = k.replace(/_/g, ' ').trim();
      return raw ? raw.replace(/\b\w/g, (c) => c.toUpperCase()) : k;
    },
    metaEntries(row) {
      const entries = [];
      if (!row) return entries;
      const base = {
        eventType: row.eventType,
        source: row.source,
        sourceId: row.sourceId,
        userId: row.userId,
      };
      Object.entries(base).forEach(([k, v]) => {
        if (v == null || v === '') return;
        entries.push({ k, v });
      });
      const meta = row.metadata || {};
      Object.entries(meta).forEach(([k, v]) => {
        if (v == null || v === '' || typeof v === 'object') return;
        entries.push({ k, v });
      });
      return entries;
    },

    async refresh() {
      await this.fetchEvents({ reset: true });
    },
    async onPaginator(event) {
      const page = Math.floor(event.first / event.rows) + 1;
      await this.applyPaginator({ page, pageSize: event.rows });
    },
    async applyFilters() {
      const [start, end] = Array.isArray(this.dateRange) ? this.dateRange : [null, null];
      await this.updateFilters({
        startDate: start,
        endDate: end,
        categories: this.selectedCategories,
        userQuery: this.userQuery,
      });
      await this.fetchEvents({ reset: true });
    },
    async clearFilters() {
      await this.resetTimeline();
      this.syncDateRangeFromFilters();
      this.selectedCategories = [];
      this.userQuery = '';
      await this.fetchEvents({ reset: true });
    },
    syncDateRangeFromFilters() {
      const { startDate, endDate } = this.filters || {};
      if (startDate && endDate) {
        this.dateRange = [
          startDate instanceof Date ? startDate : new Date(startDate),
          endDate instanceof Date ? endDate : new Date(endDate),
        ];
      } else {
        this.dateRange = null;
      }
    },
  },
};
</script>

